import React, { useEffect, useState } from "react"
import SideBarLayout from "src/v2/layout/side-bar-layout"
import { Box } from "@mui/system"
import theme from "src/v2/theme/theme"
import { Grid } from "@mui/material"
import CompanyTabsWrappedLabel from "./components/company-tab/company-tab"
import CompanyEmptyState from "./components/company-empty-state/company-empty-state"
import DatalakeHeaderEnterprise from "./components/header/Header"
import GenericModal from "src/v2/components/modal/generic-modal"
import DataGridTable from "src/v2/components/Table/data-grid-table"
import DatalakeService from "src/services/datalake-service"
import {MotorEmpresasService} from "src/services/motor-empresas-service"
import RegisterDocumentService from "src/services/register-document-service"
import LoaderOverlay from "src/v2/components/LoaderOverlay/loader-overlay"
import Loader from "src/v2/components/Loader/loader"
import { TokenService } from "src/services/token-service"
import useCompanyAutocomplete from "./hooks/use-company-autocomplete"
import CompanyTab from "./tabs/company/company-tab"
import EmissionsTab from "./tabs/emissions/emissions-tab"
import MarketToMarketTab from "./tabs/market-to-market/market-to-market-tab"
import {EconomicGroupPanel} from "./components/grupo-economico-tab/grupo-economico-tab"
import OtherDocumentsTab from "./tabs/other-documents/other-documents-tab"
import { COLUMNS_MODAL_TABLE_CNPJ } from "./constants/full-list-cnpj-const.js"
import { getCnpjRoot } from "src/v2/utils/cnpj-root"

export const DataLakeCompany = () => {
    const tokenService = new TokenService()
    const datalakeService = new DatalakeService()
    const motorEmpresasService = new MotorEmpresasService()
    const documentService = new RegisterDocumentService()

    const isAdminUser = tokenService.verifyUserIsAdmin()

    const [companyList, setCompanyList] = useState([])
    const [openModal, setOpenModal] = useState(false)
    const [loadingCompanyList, setLoadingCompanyList] = useState(false)
    const [cpfCnpjSelected, setCpfCnpjSelected] = useState(null)
    const [companyInfo, setCompanyInfo] = useState({})
    const [loadingCompanyInfo, setLoadingCompanyInfo] = useState(false)
    const [emissionData, setEmissionData] = useState([])
    const [loadingEmissions, setLoadingEmissions] = useState(false)
    const [economicGroupData, setEconomicGroupData] = useState([])
    const [loadingEconomicGroup, setLoadingEconomicGroup] = useState(false)
    const [loadingDocuments, setLoadingDocuments] = useState(false)
    //const [dataPD, setDataPD] = useState({})
    //const [loadingPd, setLoadingPd] = useState(false)
    const companyAutocomplete = useCompanyAutocomplete({
        companies: companyList,
        companiesLoading: loadingCompanyList,
        onCompanySelect: setCpfCnpjSelected,
    })

    useEffect(() => {
        const loadCompanies = async () => {
            setLoadingCompanyList(true)

            try {
                const data = await datalakeService.loadList()
                setCompanyList(data?.body?.results ?? [])
            } catch (error) {
                console.error(
                    "Erro ao carregar empresas:",
                    error
                )
                setCompanyList([])
            } finally {
                setLoadingCompanyList(false)
            }
        }

        loadCompanies()
    }, [])

    useEffect(() => {
        const cpfCnpj = cpfCnpjSelected?.CPFCNPJ

        if (!cpfCnpj) {
            return
        }

        const loadCompanyInfo = async () => {
            setOpenModal(false)
            setLoadingCompanyInfo(true)

            try {
                const data = await datalakeService.loadInfo(cpfCnpj)
                setCompanyInfo(data?.body ?? {})
            } catch (error) {
                console.error(
                    "Erro ao carregar informações da empresa:",
                    error
                )
                setCompanyInfo({})
            } finally {
                setLoadingCompanyInfo(false)
            }
        }

        loadCompanyInfo()
    }, [cpfCnpjSelected?.CPFCNPJ])

    useEffect(() => {
        const cpfCnpj = cpfCnpjSelected?.CPFCNPJ

        if (!cpfCnpj) {
            setEmissionData([])
            return
        }

        let isCurrentRequest = true

        const loadEmissions = async () => {
            setLoadingEmissions(true)

            try {
                const response =
                    await motorEmpresasService.loadEmissoes(cpfCnpj)

                const rows =
                    response?.body?.results ??
                    response?.body?.data ??
                    []

                if (isCurrentRequest) {
                    setEmissionData(
                        Array.isArray(rows) ? rows : []
                    )
                }
            } catch (error) {
                console.error(
                    "Erro ao carregar emissões da empresa:",
                    error
                )

                if (isCurrentRequest) {
                    setEmissionData([])
                }
            } finally {
                if (isCurrentRequest) {
                    setLoadingEmissions(false)
                }
            }
        }

        loadEmissions()

        return () => {
            isCurrentRequest = false
        }
    }, [cpfCnpjSelected?.CPFCNPJ])

    useEffect(() => {
        const cpfCnpj = getCnpjRoot(cpfCnpjSelected?.CPFCNPJ)

        if (!cpfCnpj) {
            setEconomicGroupData([])
            return
        }

        let isCurrentRequest = true

        const loadEconomicGroup = async () => {
            setLoadingEconomicGroup(true)
            setEconomicGroupData([])

            try {
                const response = await datalakeService.loadgrupoecon(cpfCnpj)
                const rows = response?.body?.data ?? response?.body?.results ?? []

                if (isCurrentRequest) {
                    setEconomicGroupData(Array.isArray(rows) ? rows : [])
                }
            } catch (error) {
                console.error("Erro ao carregar grupo econômico:", error)

                if (isCurrentRequest) {
                    setEconomicGroupData([])
                }
            } finally {
                if (isCurrentRequest) {
                    setLoadingEconomicGroup(false)
                }
            }
        }

        loadEconomicGroup()

        return () => {
            isCurrentRequest = false
        }
    }, [cpfCnpjSelected?.CPFCNPJ])

    const handleOpenModal = () => {
        setOpenModal(true)
    }

    const handleCloseModal = () => {
        setOpenModal(false)
    }

    return (
        <SideBarLayout title="Empresas">
            {(loadingCompanyInfo || loadingDocuments) && (
                <LoaderOverlay>
                    <Loader />
                </LoaderOverlay>
            )}
            <GenericModal
                isOpen={openModal}
                onClose={handleCloseModal}
                title="Lista de Empresas"
                width="1000px"
            >
                <DataGridTable
                    columns={COLUMNS_MODAL_TABLE_CNPJ}
                    rows={companyList}
                    loading={loadingCompanyList}
                    pageSize={25}
                    disableDeleteColumn={true}
                    getRowId={(row, index) => {
                        if (row.CPFCNPJ) return String(row.CPFCNPJ)
                        if (row.RazaoSocial)
                            return row.RazaoSocial.replace(/\W+/g, "_")
                        return `row_${index}`
                    }}
                    onRowClick={(params) => {
                        setCpfCnpjSelected(params.row)
                        setOpenModal(false)
                    }}
                />
            </GenericModal>
            <Grid
                sx={{
                    background: theme.palette.background.primary,
                    height: "100%",
                    width: "100%",
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        height: "199px",
                        left: {
                            xxxs: 0,
                            lg: 209,
                        },
                        right: 0,
                        zIndex: 0,
                    }}
                >
                    <DatalakeHeaderEnterprise
                        options={companyAutocomplete.state.results}
                        selectedValue={companyAutocomplete.state.selectedValue}
                        inputValue={companyAutocomplete.state.inputValue}
                        loading={loadingCompanyList || companyAutocomplete.state.loading}
                        error={companyAutocomplete.state.error}
                        onInputChange={companyAutocomplete.handlers.onInputChange}
                        onSelectionChange={companyAutocomplete.handlers.onSelectionChange}
                        getOptionLabel={companyAutocomplete.handlers.getOptionLabel}
                        isOptionEqualToValue={companyAutocomplete.handlers.isOptionEqualToValue}
                        handleClickFullList={handleOpenModal}
                    />
                </Box>
                {!cpfCnpjSelected?.CPFCNPJ ? (
                    <Box
                        sx={{
                            display: "flex",
                            position: "relative",
                            mt: { xxxs: 36, md: 30, lg: 22 },
                            width: "100%",
                            background: "#272D42",
                            flexDirection: {
                                xxxs: "column",
                            },
                        }}
                    >
                        <CompanyEmptyState
                            handleClickFullList={handleOpenModal}
                        />
                    </Box>
                ) : (
                    <Box
                        sx={{
                            display: "flex",
                            position: "relative",
                            mt: { xxxs: 36, md: 30, lg: 22 },
                            width: "100%",
                            background: "#272D42",
                            flexDirection: {
                                xxxs: "column",
                            },
                        }}
                    >
                        <Box>
                            <CompanyTabsWrappedLabel
                                companyTabChildren={
                                    <CompanyTab
                                        companyData={companyInfo}
                                        isAdminUser={isAdminUser}
                                    />
                                }
                                marketToMarketTabChildren={
                                    <MarketToMarketTab
                                        company={cpfCnpjSelected}
                                    />
                                }
                                emissionTabChildren={
                                    <EmissionsTab
                                        data={emissionData}
                                        loading={loadingEmissions}
                                        isAdminUser={isAdminUser}
                                    />
                                }
                                economicGroupTabChildren={
                                    <EconomicGroupPanel
                                        data={economicGroupData}
                                        loading={loadingEconomicGroup}
                                        isAdminUser={isAdminUser}
                                    />
                                }
                                otherDocumentsTabChildren={
                                    <OtherDocumentsTab
                                        cetip={cpfCnpjSelected?.CPFCNPJ}
                                        isAdminUser={isAdminUser}
                                        documentService={documentService}
                                        setIsLoading={setLoadingDocuments}
                                    />
                                }
                            />
                        </Box>
                    </Box>
                )}
            </Grid>
        </SideBarLayout>
    )
}

export default DataLakeCompany